import React, { useState, useEffect } from "react";
import { api } from "../../api/client";

const TARGET_MODES = [
  { value: "all", label: "All Passengers" },
  { value: "include", label: "Only Selected Passengers" },
  { value: "exclude", label: "All Except Selected Passengers" }
];

const normalizeAnnouncement = (announcement) => ({
  ...announcement,
  passengerTargetMode: announcement.passengerTargetMode || "all",
  passengerTargetUsers: Array.isArray(announcement.passengerTargetUsers) ? announcement.passengerTargetUsers : []
});

const TargetModeSelector = ({ value, onChange, disabled = false }) => (
  <div
    style={{
      display: "grid",
      gap: "0.5rem",
      gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
      marginBottom: "0.5rem"
    }}
  >
    {TARGET_MODES.map((mode) => {
      const active = value === mode.value;
      return (
        <button
          key={mode.value}
          type="button"
          onClick={() => onChange(mode.value)}
          disabled={disabled}
          style={{
            textAlign: "left",
            border: active ? "1px solid #2d7a64" : "1px solid #d1d5db",
            background: active ? "#ecfdf5" : "#fff",
            color: active ? "#065f46" : "#374151",
            borderRadius: "8px",
            padding: "0.6rem 0.7rem",
            fontSize: "0.83rem",
            fontWeight: active ? 700 : 600,
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.6 : 1,
            transition: "all 0.15s ease"
          }}
        >
          {mode.label}
        </button>
      );
    })}
  </div>
);

const PassengerPicker = ({ passengers, selectedIds, onChange }) => {
  const [query, setQuery] = useState("");
  const selectedSet = new Set(selectedIds || []);
  const filtered = passengers.filter((p) => {
    const text = `${p.name} ${p.email}`.toLowerCase();
    return text.includes(query.toLowerCase());
  });

  const togglePassenger = (id) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  };

  return (
    <div style={{ border: "1px solid #d1d5db", borderRadius: "6px", padding: "0.5rem", background: "#fff" }}>
      <input
        type="text"
        placeholder="Search passenger..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        style={{ width: "100%", marginBottom: "0.5rem" }}
      />
      <div style={{ display: "flex", gap: "0.4rem", marginBottom: "0.5rem", flexWrap: "wrap" }}>
        <button
          type="button"
          className="btn"
          style={{ fontSize: "0.75rem", padding: "0.2rem 0.45rem", textTransform: "none" }}
          onClick={() => onChange(passengers.map((p) => p._id))}
        >
          Select All
        </button>
        <button
          type="button"
          className="btn secondary"
          style={{ fontSize: "0.75rem", padding: "0.2rem 0.45rem", textTransform: "none" }}
          onClick={() => onChange([])}
        >
          Clear
        </button>
      </div>
      <div style={{ maxHeight: "220px", overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: "4px" }}>
        {filtered.length === 0 ? (
          <div style={{ fontSize: "0.8rem", color: "#6b7280", padding: "0.35rem" }}>No passengers found</div>
        ) : (
          <table className="table" style={{ margin: 0 }}>
            <thead>
              <tr>
                <th style={{ width: "36px", padding: "0.3rem" }}>
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && filtered.every((p) => selectedSet.has(p._id))}
                    onChange={() => {
                      const allFilteredSelected = filtered.length > 0 && filtered.every((p) => selectedSet.has(p._id));
                      const next = new Set(selectedSet);
                      if (allFilteredSelected) {
                        filtered.forEach((p) => next.delete(p._id));
                        onChange([...next]);
                        return;
                      }
                      filtered.forEach((p) => next.add(p._id));
                      onChange([...next]);
                    }}
                    style={{ accentColor: "#2d7a64" }}
                  />
                </th>
                <th style={{ padding: "0.3rem", fontSize: "0.78rem" }}>Name</th>
                <th style={{ padding: "0.3rem", fontSize: "0.78rem" }}>Email</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p._id}>
                  <td style={{ padding: "0.22rem", textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={selectedSet.has(p._id)}
                      onChange={() => togglePassenger(p._id)}
                      style={{ accentColor: "#2d7a64" }}
                    />
                  </td>
                  <td style={{ padding: "0.22rem", fontSize: "0.8rem", whiteSpace: "nowrap" }}>{p.name}</td>
                  <td style={{ padding: "0.22rem", fontSize: "0.78rem", color: "#4b5563" }}>{p.email}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div style={{ marginTop: "0.4rem", fontSize: "0.8rem", color: "#374151" }}>
        Selected: {selectedSet.size}
      </div>
    </div>
  );
};

const TabAnnouncements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [passengers, setPassengers] = useState([]);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    message: "",
    showToPassengers: false,
    showToDrivers: false,
    passengerTargetMode: "all",
    passengerTargetUsers: []
  });

  const load = async () => {
    const [announcementsRes, usersRes] = await Promise.all([
      api("/admin/announcements"),
      api("/admin/users/all")
    ]);
    setAnnouncements((announcementsRes.announcements || []).map(normalizeAnnouncement));
    setPassengers((usersRes.users || []).filter((u) => u.role === "passenger" && u.status === "approved"));
  };

  useEffect(() => {
    load();
  }, []);

  const createAnnouncement = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) {
      alert("Message is required");
      return;
    }
    try {
      await api("/admin/announcements", { method: "POST", body: form });
      setForm({
        message: "",
        showToPassengers: false,
        showToDrivers: false,
        passengerTargetMode: "all",
        passengerTargetUsers: []
      });
      load();
    } catch (err) {
      alert(err.message || "Failed to create announcement");
    }
  };

  const updateAnnouncement = async (id) => {
    const announcement = announcements.find((a) => a._id === id);
    if (!announcement) return;
    try {
      await api(`/admin/announcements/${id}`, {
        method: "PUT",
        body: {
          message: announcement.message,
          showToPassengers: announcement.showToPassengers,
          showToDrivers: announcement.showToDrivers,
          passengerTargetMode: announcement.passengerTargetMode,
          passengerTargetUsers: announcement.passengerTargetUsers
        }
      });
      setEditId(null);
      load();
    } catch (err) {
      alert(err.message || "Failed to update announcement");
    }
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await api(`/admin/announcements/${id}`, { method: "DELETE" });
      load();
    } catch (err) {
      alert(err.message || "Failed to delete announcement");
    }
  };

  const updateField = (id, field, value) => {
    setAnnouncements((prev) => prev.map((a) => (a._id === id ? { ...a, [field]: value } : a)));
  };

  const updateMode = (id, mode) => {
    setAnnouncements((prev) =>
      prev.map((a) =>
        a._id === id
          ? {
              ...a,
              passengerTargetMode: mode,
              passengerTargetUsers: mode === "all" ? [] : a.passengerTargetUsers
            }
          : a
      )
    );
  };

  const updateFormMode = (mode) => {
    setForm((prev) => ({
      ...prev,
      passengerTargetMode: mode,
      passengerTargetUsers: mode === "all" ? [] : prev.passengerTargetUsers
    }));
  };

  const getTargetSummary = (announcement) => {
    if (!announcement.showToPassengers) return "-";
    if (announcement.passengerTargetMode === "include") {
      return `Only selected (${announcement.passengerTargetUsers.length})`;
    }
    if (announcement.passengerTargetMode === "exclude") {
      return `All except selected (${announcement.passengerTargetUsers.length})`;
    }
    return "All passengers";
  };

  return (
    <div className="card">
      <div style={{ marginBottom: "2rem" }}>
        <h4>Create New Announcement</h4>
        <form onSubmit={createAnnouncement}>
          <label>Message</label>
          <textarea
            value={form.message}
            onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
            placeholder="Enter announcement message..."
            rows={3}
            style={{
              width: "100%",
              padding: "0.5rem",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              fontSize: "0.875rem",
              resize: "vertical"
            }}
            required
          />

          <div style={{ marginTop: "1rem", display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <input
                type="checkbox"
                checked={form.showToPassengers}
                onChange={(e) => setForm((prev) => ({ ...prev, showToPassengers: e.target.checked }))}
                style={{ accentColor: "#2d7a64" }}
              />
              Show to Passengers
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <input
                type="checkbox"
                checked={form.showToDrivers}
                onChange={(e) => setForm((prev) => ({ ...prev, showToDrivers: e.target.checked }))}
                style={{ accentColor: "#2d7a64" }}
              />
              Show to Drivers
            </label>
          </div>

          {form.showToPassengers && (
            <div style={{ marginTop: "1rem" }}>
              <label>Passenger Target</label>
              <TargetModeSelector value={form.passengerTargetMode} onChange={updateFormMode} />
              {form.passengerTargetMode !== "all" && (
                <PassengerPicker
                  passengers={passengers}
                  selectedIds={form.passengerTargetUsers}
                  onChange={(selected) => setForm((prev) => ({ ...prev, passengerTargetUsers: selected }))}
                />
              )}
            </div>
          )}

          <button type="submit" className="btn success" style={{ marginTop: "1rem", textTransform: "none" }}>
            Create Announcement
          </button>
        </form>
      </div>

      <h4>Existing Announcements</h4>
      {announcements.length === 0 ? (
        <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>No announcements yet</p>
      ) : (
        <div className="scroll-x">
          <table className="table">
            <thead>
              <tr>
                <th>Message</th>
                <th>Show to Passengers</th>
                <th>Passenger Target</th>
                <th>Show to Drivers</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {announcements.map((a) => (
                <tr key={a._id}>
                  <td>
                    {editId === a._id ? (
                      <textarea
                        value={a.message}
                        onChange={(e) => updateField(a._id, "message", e.target.value)}
                        rows={2}
                        style={{
                          width: "100%",
                          padding: "0.5rem",
                          border: "1px solid #d1d5db",
                          borderRadius: "4px",
                          fontSize: "0.875rem"
                        }}
                      />
                    ) : (
                      <div style={{ whiteSpace: "pre-wrap" }}>{a.message}</div>
                    )}
                  </td>
                  <td>
                    {editId === a._id ? (
                      <input
                        type="checkbox"
                        checked={a.showToPassengers}
                        onChange={(e) => updateField(a._id, "showToPassengers", e.target.checked)}
                        style={{ accentColor: "#2d7a64" }}
                      />
                    ) : (
                      <span>{a.showToPassengers ? "✓" : "-"}</span>
                    )}
                  </td>
                  <td style={{ minWidth: "280px" }}>
                    {editId === a._id ? (
                      <div>
                        <TargetModeSelector
                          value={a.passengerTargetMode}
                          onChange={(mode) => updateMode(a._id, mode)}
                          disabled={!a.showToPassengers}
                        />
                        {a.showToPassengers && a.passengerTargetMode !== "all" && (
                          <PassengerPicker
                            passengers={passengers}
                            selectedIds={a.passengerTargetUsers}
                            onChange={(selected) => updateField(a._id, "passengerTargetUsers", selected)}
                          />
                        )}
                      </div>
                    ) : (
                      <span>{getTargetSummary(a)}</span>
                    )}
                  </td>
                  <td>
                    {editId === a._id ? (
                      <input
                        type="checkbox"
                        checked={a.showToDrivers}
                        onChange={(e) => updateField(a._id, "showToDrivers", e.target.checked)}
                        style={{ accentColor: "#2d7a64" }}
                      />
                    ) : (
                      <span>{a.showToDrivers ? "✓" : "-"}</span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                      {editId === a._id ? (
                        <>
                          <button
                            className="btn success"
                            style={{ fontSize: "0.8rem", padding: "0.3rem 0.7rem", textTransform: "none" }}
                            onClick={() => updateAnnouncement(a._id)}
                          >
                            Save
                          </button>
                          <button
                            className="btn secondary"
                            style={{ fontSize: "0.8rem", padding: "0.3rem 0.7rem", textTransform: "none" }}
                            onClick={() => {
                              setEditId(null);
                              load();
                            }}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="btn"
                            style={{ fontSize: "0.8rem", padding: "0.3rem 0.7rem", textTransform: "none" }}
                            onClick={() => setEditId(a._id)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn danger"
                            style={{ fontSize: "0.8rem", padding: "0.3rem 0.7rem", textTransform: "none" }}
                            onClick={() => deleteAnnouncement(a._id)}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TabAnnouncements;
